import { yupResolver } from "@hookform/resolvers/yup";
import { Divider } from "@omni/ui/components/Divider/divider.component";
import { cn } from "@omni/utils/styles";
import { useId } from "react";

import { RadioFieldArray } from "@/components/Form/RHF/FormFields/RadioFieldArray/radioFieldArray.component";
import { TextField } from "@/components/Form/RHF/FormFields/TextField/Textfield.component";
import { useOmniForm } from "@/components/Form/RHF/Utils/useOmniForm";
import { WishlistModel } from "@/redux/modules/api/data/wishlist/@types/wishlist.model";
import { isFull } from "@/redux/modules/api/data/wishlist/wishlist.utils";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { FormInfoProvider } from "@/utils/forms/formInfo.provider";
import { OmniFormContextProvider } from "@/utils/forms/omniForm.provider";

import { MAX_WISHLISTS } from "../wishlist.config";
import {
    WishListActionableFormValidationSchema,
    WishListActionableFormValues
} from "./WishListActionable.schema";
import { WishlistActionableMode } from "./WishListActionable.types";

export interface WishListActionableProps {
    formId: string;
    wishlists: WishlistModel[];
    loading: boolean;
    currentWishlistId?: string;
    mode: WishlistActionableMode;
    submit: ({
        wishlistId,
        wishlistName
    }: {
        wishlistId?: string;
        wishlistName?: string;
    }) => void;
}

const WishListActionable = ({
    formId,
    wishlists,
    loading,
    currentWishlistId,
    mode,
    submit
}: WishListActionableProps) => {
    const { t } = useGranite();

    const id = useId();

    const isDisabled = (wishlist: WishlistModel) =>
        (mode === WishlistActionableMode.MOVE &&
            currentWishlistId === wishlist.id) ||
        isFull(wishlist);

    const methods = useOmniForm({
        mode: "onBlur",
        reValidateMode: "onBlur",
        defaultValues: {
            targetId: wishlists.find((w) => !isDisabled(w))?.id || "new",
            name: ""
        },
        resolver: yupResolver(WishListActionableFormValidationSchema)
    });

    if (loading) {
        return (
            <div className={cn("tw:flex tw:items-center tw:justify-center")}>
                <i className={cn("las la-3x la-spinner la-pulse")} />
            </div>
        );
    }

    const onSubmit = (values: WishListActionableFormValues) => {
        return submit({
            wishlistId: values.targetId === id ? undefined : values.targetId,
            wishlistName: values.targetId === id ? values.name : undefined
        });
    };

    const allowCreateNew = wishlists.length < MAX_WISHLISTS;

    return (
        <FormInfoProvider
            formId={formId}
            withNeverbounce={false}
        >
            {() => (
                <OmniFormContextProvider {...methods}>
                    <form
                        id={formId}
                        onSubmit={methods.handleSubmit(onSubmit)}
                    >
                        <RadioFieldArray<WishListActionableFormValues, string>
                            name="targetId"
                            required={true}
                            label=""
                            hideLabel={true}
                            direction="vertical"
                            hideMicroCopy={true}
                            options={wishlists.map((wishlist) => ({
                                label: wishlist.name,
                                value: wishlist.id,
                                disabled: isDisabled(wishlist)
                            }))}
                            renderLabel={(option, index) => {
                                const wishList = wishlists.find(
                                    (wl) => wl.id === option.value
                                );

                                const isLast = index === wishlists.length - 1;

                                return (
                                    <div
                                        className={cn({
                                            "tw:text-muted": option.disabled
                                        })}
                                    >
                                        <h5
                                            className={cn(
                                                "tw:overflow-hidden tw:mb-1 tw:mt-1"
                                            )}
                                        >
                                            {option.label}
                                        </h5>
                                        <small
                                            className={cn("tw:block", {
                                                "tw:mb-4": !isLast
                                            })}
                                        >
                                            {t(
                                                "wishlist.productCount",
                                                (wishList?.products || [])
                                                    .length
                                            )}
                                        </small>
                                    </div>
                                );
                            }}
                        />
                        {allowCreateNew && (
                            <>
                                <Divider
                                    marginTop="0"
                                    className={cn("tw:mb-4")}
                                />
                                <RadioFieldArray<
                                    WishListActionableFormValues,
                                    string
                                >
                                    name="targetId"
                                    required={true}
                                    label=""
                                    hideLabel={true}
                                    direction="vertical"
                                    hideMicroCopy={true}
                                    options={[
                                        {
                                            label: t("wishlist.newWishlist"),
                                            value: id
                                        }
                                    ]}
                                    renderLabel={(option) => {
                                        return (
                                            <div
                                                onClick={() =>
                                                    methods.setValue(
                                                        "targetId",
                                                        option.value
                                                    )
                                                }
                                            >
                                                <TextField<WishListActionableFormValues>
                                                    label={t(
                                                        "wishlist.newWishlist"
                                                    )}
                                                    name="name"
                                                    required={false}
                                                    placeholder={t(
                                                        "wishlist.name.placeholder"
                                                    )}
                                                    optionalIndication={true}
                                                />
                                            </div>
                                        );
                                    }}
                                />
                            </>
                        )}
                    </form>
                </OmniFormContextProvider>
            )}
        </FormInfoProvider>
    );
};

export { WishListActionable };
